import { useState } from "react";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getOrderById } from "../../api/orderService";
import LoadingScreen from "../../components/loadingScreen";
import { routeEndpoints } from "../../routes/routeEndpoints";
import AccountBenefits from "./components/accountBenefits";
import ConfirmedShippingDetails from "./components/confirmedShippingDetails";
import FacilityAddress from "./components/facilityAddress";
import ShippedChoice from "./components/shippedChoice";
import TrackingEntryForm from "./components/trackingEntryForm";
import { useUpdateShippingStatus } from "../../hooks/useUpdateShippingStatus";

export default function ShippingStatusPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("id");

  const [showTracking, setShowTracking] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => getOrderById(orderId!),
    enabled: !!orderId,
  });

  const updateMutation = useUpdateShippingStatus(orderId!, {
    onSuccess: () => setShowTracking(false),
  });

  if (isLoading) return <LoadingScreen />;

  if (!data || !orderId) return null;

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col md:p-6 p-4 font-sans">
      <div className="w-full max-w-xl md:ml-28 mt-9 mb-12">
        <button
          onClick={() =>
            navigate(`${routeEndpoints.order}?id=${orderId}`, {
              state: { fromStatus: true },
            })
          }
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors font-medium cursor-pointer"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to order details
        </button>
      </div>

      {!data.trackingInfo?.trackingNumber ? (
        <div className="w-full max-w-xl mx-auto space-y-6">
          <FacilityAddress />

          <div className="bg-white rounded-xl md:px-10 px-4 py-7 shadow-sm border border-slate-100">
            <h2 className="md:text-2xl text-lg font-medium text-slate-800 mb-8 text-center mx-auto">
              Already shipped your item?
            </h2>
            <ShippedChoice
              isShipped={showTracking}
              onChange={setShowTracking}
            />

            {showTracking ? (
              <TrackingEntryForm
                orderId={orderId}
                submitLabel={
                  updateMutation.isPending ? "Confirming..." : "Confirm"
                }
                isSubmitting={updateMutation.isPending}
                onSubmit={(submission) => updateMutation.mutate(submission)}
              />
            ) : (
              <AccountBenefits
                title="Need a shipping label?"
                subtitle="Create a free account to continue."
                benefits={[
                  "Get shipping labels quickly",
                  "Track your repair in real time",
                  "Receive status updates by email",
                  "View all your orders in one place",
                ]}
              />
            )}
          </div>
        </div>
      ) : (
        <ConfirmedShippingDetails
          orderId={orderId}
          trackingInfo={data.trackingInfo}
        />
      )}
    </div>
  );
}
